import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useLang } from "../i18n";
import ProductCard from "../components/ProductCard";

const CATS = ["tutti", "mieli", "polline", "propoli", "regalo"];

export default function Shop() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "tutti";

  useEffect(() => {
    setLoading(true);
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = cat === "tutti" ? [...products] : products.filter((p) => p.category === cat);
    if (sort === "asc") list.sort((a, b) => a.sizes[0].price - b.sizes[0].price);
    else if (sort === "desc") list.sort((a, b) => b.sizes[0].price - a.sizes[0].price);
    else list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    return list;
  }, [products, cat, sort]);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid="shop-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight" data-testid="shop-title">{t("shop.title")}</h1>
        <p className="mt-4 text-cocoa/60 max-w-xl">{t("shop.sub")}</p>
      </motion.div>
      <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          {CATS.map((c) => (
            <button key={c} onClick={() => setParams(c === "tutti" ? {} : { cat: c })} data-testid={`filter-${c}`}
              className={`px-4 py-2 text-sm font-medium border transition-colors duration-200 ${cat === c ? "bg-bark text-parchment border-bark" : "border-[#E5D9C8] text-cocoa/70 hover:border-honey hover:text-honey"}`}>
              {c === "tutti" ? t("shop.all") : t(`cat.${c}`)}
            </button>
          ))}
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select"
          className="bg-white border border-[#E5D9C8] px-4 py-2 text-sm text-cocoa/80 focus:outline-none focus:border-honey">
          <option value="featured">{t("shop.sort.featured")}</option>
          <option value="asc">{t("shop.sort.asc")}</option>
          <option value="desc">{t("shop.sort.desc")}</option>
        </select>
      </div>
      {loading ? (
        <p className="mt-20 text-center text-cocoa/50 font-mono text-sm" data-testid="shop-loading">{t("common.loading")}</p>
      ) : (
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="product-grid">
          {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
